/* eslint-disable no-empty */
'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type InputHTMLAttributes,
  type Ref,
} from 'react';

export type FileMetadata = {
  name: string;
  size: number;
  type: string;
  url: string;
  id: string;
};

export type FileWithPreview = {
  file: File | FileMetadata;
  id: string;
  preview?: string;
};

export type FileUploadOptions = {
  maxFiles?: number;
  maxSize?: number; // in bytes
  accept?: string;
  multiple?: boolean;
  initialFiles?: FileMetadata[];
  onFilesChange?: (files: FileWithPreview[]) => void;
  onFilesAdded?: (addedFiles: FileWithPreview[]) => void;
};

export type FileUploadState = {
  files: FileWithPreview[];
  isDragging: boolean;
  errors: string[];
  isCompressing: boolean;
};

export type FileUploadActions = {
  addFiles: (files: FileList | File[]) => void;
  removeFile: (id: string) => void;
  clearFiles: () => void;
  clearErrors: () => void;
  handleDragEnter: (e: DragEvent<HTMLElement>) => void;
  handleDragLeave: (e: DragEvent<HTMLElement>) => void;
  handleDragOver: (e: DragEvent<HTMLElement>) => void;
  handleDrop: (e: DragEvent<HTMLElement>) => void;
  handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  openFileDialog: () => void;
  openCameraDialog: () => void;
  getInputProps: (
    props?: InputHTMLAttributes<HTMLInputElement>
  ) => InputHTMLAttributes<HTMLInputElement> & {
    ref: Ref<HTMLInputElement>;
  };
};

// Helper function to detect Android
export const isAndroid = () => /Android/i.test(navigator.userAgent);
const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent);

// Constants
const COMPRESSION_TIMEOUT = 15000; // 15 seconds per image
const MAX_COMPRESSION_SIZE = 1 * 1024 * 1024; // 1MB - max size after compression
const IOS_MAX_DIMENSION = 1200;
const ANDROID_MAX_DIMENSION = 1600;

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const useFileUpload = (
  options: FileUploadOptions = {}
): [FileUploadState, FileUploadActions] => {
  const {
    maxFiles = Infinity,
    maxSize = Infinity,
    accept = '*',
    multiple = false,
    initialFiles = [],
    onFilesChange,
    onFilesAdded,
  } = options;

  const [state, setState] = useState<FileUploadState>({
    files: initialFiles.map((f) => ({ file: f, id: f.id, preview: f.url })),
    isDragging: false,
    errors: [],
    isCompressing: false,
  });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // cleanup previews
      state.files.forEach((f) => {
        if (f.preview && f.file instanceof File) {
          try {
            URL.revokeObjectURL(f.preview);
          } catch (e) {
            // noop
          }
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateUniqueId = useCallback(
    () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    []
  );

  const validateFile = useCallback(
    (file: File | FileMetadata): string | null => {
      if (file.size > maxSize) {
        return `Ảnh "${file.name}" vượt quá kích thước tối đa ${formatBytes(maxSize)}.`;
      }

      if (accept === '*') return null;

      const acceptedTypes = accept.split(',').map((t) => t.trim());
      const fileType = file instanceof File ? file.type || '' : file.type;
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

      const isAccepted = acceptedTypes.some((type) => {
        if (type.startsWith('.')) return fileExtension === type.toLowerCase();
        if (type.endsWith('/*')) {
          const base = type.split('/')[0];
          return fileType.startsWith(`${base}/`);
        }
        return fileType === type;
      });

      return isAccepted ? null : `Ảnh "${file.name}" không đúng định dạng.`;
    },
    [accept, maxSize]
  );

  // compress a single image with timeout and proper cleanup
  const compressImage = useCallback(async (file: File): Promise<File> => {
    if (file.size <= MAX_COMPRESSION_SIZE) return file;

    return new Promise<File>((resolve, reject) => {
      const timeout = window.setTimeout(
        () => reject(new Error('Compression timeout')),
        COMPRESSION_TIMEOUT
      );
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;

      img.onload = () => {
        try {
          const maxDim = isIOS() ? IOS_MAX_DIMENSION : ANDROID_MAX_DIMENSION;
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            const ratio = Math.min(maxDim / width, maxDim / height);
            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas not supported');

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          // Try multiple quality levels to get under MAX_COMPRESSION_SIZE
          const tryCompress = async (quality: number): Promise<Blob | null> => {
            return new Promise((res) => {
              canvas.toBlob(
                (blob) => {
                  res(blob);
                },
                file.type,
                quality
              );
            });
          };

          void (async () => {
            try {
              // Try progressively lower quality until we get under 1MB
              const qualities = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3];
              let finalBlob: Blob | null = null;

              for (const quality of qualities) {
                const blob = await tryCompress(quality);
                if (blob && blob.size <= MAX_COMPRESSION_SIZE) {
                  finalBlob = blob;
                  break;
                }
              }

              window.clearTimeout(timeout);
              try {
                URL.revokeObjectURL(objectUrl);
              } catch (e) {
                // noop
              }

              if (finalBlob) {
                const compressed = new File([finalBlob], file.name, {
                  type: file.type,
                });
                resolve(compressed);
              } else {
                reject(
                  new Error(
                    `Ảnh có kích thước quá lớn. Vui lòng chọn ảnh khác.`
                  )
                );
              }
            } catch (err) {
              window.clearTimeout(timeout);
              try {
                URL.revokeObjectURL(objectUrl);
              } catch (e) {}
              reject(err);
            }
          })();
        } catch (err) {
          window.clearTimeout(timeout);
          try {
            URL.revokeObjectURL(objectUrl);
          } catch (e) {}
          reject(err);
        }
      };

      img.onerror = () => {
        window.clearTimeout(timeout);
        try {
          URL.revokeObjectURL(objectUrl);
        } catch (e) {}
        reject(new Error('Image load failed'));
      };
    });
  }, []);

  // Process single file at a time with immediate compression
  const processFiles = useCallback(
    async (incomingFiles: File[]) => {
      // Only process first file
      const file = incomingFiles[0];
      if (!file) return { validFiles: [], errors: [] };

      try {
        // Check for duplicate
        const duplicate = state.files.some(
          (s) =>
            s.file instanceof File &&
            s.file.name === file.name &&
            s.file.size === file.size
        );
        if (duplicate) {
          return {
            validFiles: [],
            errors: [`Ảnh "${file.name}" đã tồn tại.`],
          };
        }

        let processed = file;

        // Compress image if it's an image type
        if (file.type.startsWith('image/')) {
          try {
            processed = await compressImage(file);
          } catch (e) {
            const errorMsg =
              e instanceof Error
                ? e.message
                : `Không thể xử lý ảnh "${file.name}". Vui lòng thử ảnh khác.`;
            return { validFiles: [], errors: [errorMsg] };
          }
        }

        // Validate the processed file
        const validationError = validateFile(processed);
        if (validationError) {
          return { validFiles: [], errors: [validationError] };
        }

        const preview = URL.createObjectURL(processed);
        return {
          validFiles: [{ file: processed, id: generateUniqueId(), preview }],
          errors: [],
        };
      } catch (e) {
        console.error('File processing error:', e);
        return {
          validFiles: [],
          errors: ['Có lỗi xảy ra khi xử lý ảnh.'],
        };
      }
    },
    [compressImage, generateUniqueId, state.files, validateFile]
  );

  // addFiles now processes one file at a time with immediate compression
  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const arr = Array.from(newFiles || []);
      if (arr.length === 0) return;

      // Only process first file
      const firstFile = arr[0];

      // Check if already at max files
      setState((prev) => {
        if (prev.files.length >= maxFiles) {
          return {
            ...prev,
            errors: [`Bạn đã đạt giới hạn ${maxFiles} ảnh.`],
          };
        }
        return { ...prev, isCompressing: true, errors: [] };
      });

      void (async () => {
        try {
          const { validFiles, errors } = await processFiles([firstFile]);

          if (!mountedRef.current) return;

          setState((prev) => {
            // If there are errors, don't add the file
            if (errors.length > 0) {
              return {
                ...prev,
                errors,
                isCompressing: false,
              };
            }

            const newList = [...prev.files, ...validFiles];

            // Ensure we don't exceed maxFiles
            const limited =
              maxFiles !== Infinity ? newList.slice(0, maxFiles) : newList;

            if (validFiles.length > 0) {
              onFilesAdded?.(validFiles);
              onFilesChange?.(limited);
            }

            return {
              ...prev,
              files: limited,
              errors: [],
              isCompressing: false,
            };
          });
        } catch (e) {
          console.error('File processing error', e);
          if (!mountedRef.current) return;
          setState((prev) => ({
            ...prev,
            errors: ['Có lỗi xảy ra khi xử lý ảnh.'],
            isCompressing: false,
          }));
        } finally {
          // reset input value to allow re-uploading same file
          try {
            if (inputRef.current) inputRef.current.value = '';
          } catch (e) {}
        }
      })();
    },
    [maxFiles, multiple, onFilesAdded, onFilesChange, processFiles]
  );

  const removeFile = useCallback(
    (id: string) => {
      setState((prev) => {
        const fileToRemove = prev.files.find((f) => f.id === id);
        if (fileToRemove?.preview && fileToRemove.file instanceof File) {
          try {
            URL.revokeObjectURL(fileToRemove.preview);
          } catch (e) {}
        }
        const newFiles = prev.files.filter((f) => f.id !== id);
        onFilesChange?.(newFiles);
        return { ...prev, files: newFiles, errors: [] };
      });
    },
    [onFilesChange]
  );

  const clearFiles = useCallback(() => {
    setState((prev) => {
      prev.files.forEach((f) => {
        if (f.preview && f.file instanceof File) {
          try {
            URL.revokeObjectURL(f.preview);
          } catch (e) {}
        }
      });
      if (inputRef.current) inputRef.current.value = '';
      onFilesChange?.([]);
      return { ...prev, files: [], errors: [], isCompressing: false };
    });
  }, [onFilesChange]);

  const clearErrors = useCallback(
    () => setState((prev) => ({ ...prev, errors: [] })),
    []
  );

  const handleDragEnter = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setState((prev) => ({ ...prev, isDragging: true }));
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setState((prev) => ({ ...prev, isDragging: false }));
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setState((prev) => ({ ...prev, isDragging: false }));
      const files = Array.from(e.dataTransfer.files || []);
      // Always process only the first file
      if (files.length > 0) {
        addFiles([files[0]]);
      }
    },
    [addFiles]
  );

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) addFiles(files);
    },
    [addFiles]
  );

  const openFileDialog = useCallback(() => inputRef.current?.click(), []);

  const openCameraDialog = useCallback(() => {
    const cameraInput = document.createElement('input');
    cameraInput.type = 'file';
    cameraInput.accept = 'image/*';
    cameraInput.capture = 'environment';
    // Always single file upload
    cameraInput.multiple = false;

    const handler = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        // Process only first file
        addFiles([target.files[0]]);
      }
      cameraInput.removeEventListener('change', handler);
      cameraInput.remove();
    };

    cameraInput.addEventListener('change', handler);
    cameraInput.click();
  }, [addFiles]);

  const getInputProps = useCallback(
    (props: InputHTMLAttributes<HTMLInputElement> = {}) => ({
      ...props,
      type: 'file' as const,
      onChange: handleFileChange,
      accept: props.accept ?? accept,
      // Always single file upload
      multiple: false,
      ref: inputRef,
    }),
    [accept, handleFileChange]
  );

  return [
    state,
    {
      addFiles: (files) => addFiles(files),
      removeFile,
      clearFiles,
      clearErrors,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      handleFileChange,
      openFileDialog,
      openCameraDialog,
      getInputProps,
    },
  ];
};
